// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ElectionData } from './data.ts';
import { fmtInt } from './utils/format.ts';

let overlay: HTMLDivElement | null = null;
let modal: HTMLDivElement | null = null;

function close(): void {
  modal?.classList.remove('visible');
  overlay?.classList.remove('visible');
  const ov = overlay, md = modal;
  overlay = null; modal = null;
  setTimeout(() => { ov?.remove(); md?.remove(); }, 260);
}

export function openAbout(data: ElectionData): void {
  overlay?.remove(); modal?.remove();
  overlay = document.createElement('div');
  overlay.className = 'overlay';
  document.body.appendChild(overlay);

  modal = document.createElement('div');
  modal.className = 'modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-label', 'About this site');
  const gen = new Date(data.generated).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });

  modal.innerHTML = `
    <button class="close" aria-label="Close">×</button>
    <h2>About this site</h2>
    <p>An independent, reader-friendly view of the official results of the <strong>2025 Australian federal election</strong> — the House of Representatives, all 150 seats. It unifies the Australian Electoral Commission's separate result files into one place you can search, map, rank and drill into.</p>

    <h3>What the numbers mean</h3>
    <ul>
      <li><strong>First preferences</strong> are the primary vote — the “1” on the ballot.</li>
      <li><strong>Two-candidate-preferred (TCP)</strong> is the final count between the last two candidates in a seat; it decides who won and by what margin.</li>
      <li><strong>Two-party-preferred (TPP)</strong> is the notional Labor-vs-Coalition split. The AEC calculates it for every seat, even ones the majors did not finish first and second in, so seats can be compared on one line.</li>
      <li><strong>Swing</strong> is the change in the two-party vote since the 2022 election, adjusted for boundary redistributions.</li>
    </ul>

    <h3>Data source</h3>
    <p>Every figure comes from the <a href="https://results.aec.gov.au/31496/Website/HouseDefault-31496.htm" target="_blank" rel="noopener">AEC Tally Room</a> (event 31496), final results. Electoral-division boundaries are the AEC's, via the Digital Atlas of Australia. AEC data is © the Commonwealth of Australia, licensed CC BY 4.0.</p>
    <p>Results loaded here were generated by the AEC on <strong>${gen}</strong>. Final results do not change, so the data is static between elections; the pipeline re-checks the source yearly.</p>

    <h3>How it's built</h3>
    <p>The AEC's CSV exports are parsed by a small dependency-free pipeline that reconciles independently-published totals (national seat count, two-party vote, per-party seats) before publishing. All charts are hand-drawn; the map uses Leaflet. No accounts, no tracking cookies — just cookie-less, anonymous page-view counts.</p>
    <p style="color:var(--text-tertiary);font-size:var(--fs-sm)">This is not affiliated with or endorsed by the Australian Electoral Commission. ${fmtInt(data.national.turnout)} people voted; for anything official, see the AEC.</p>`;

  document.body.appendChild(modal);
  void modal.offsetWidth;
  overlay.classList.add('visible');
  modal.classList.add('visible');

  overlay.addEventListener('click', close);
  modal.querySelector('.close')!.addEventListener('click', close);
  const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { close(); document.removeEventListener('keydown', onKey); } };
  document.addEventListener('keydown', onKey);
}
